import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { MaterialRate } from '~hooks/useMaterialRates/models';
import useMaterialRates from '~hooks/useMaterialRates/useMaterialRates';
import connection from '~services/connectionModule';
import { Pagination } from '~services/pagination';

interface MaterialRateGridState {
  isLoading: boolean;
  materialRates: MaterialRate[];
  pagination?: Pagination;
}

interface MaterialRatesDataGridProps {
  companyId: string;
  customerId?: string;
  materialIds?: string[];
  onDelete: (materialRate: MaterialRate) => void;
  onEdit: (materialRate: MaterialRate) => void;
  projectIds?: string[];
  siteIds?: string[];
}

export type MaterialRatesDataGridHandler = {
  refetch: () => void;
};

const MaterialRatesDataGrid = forwardRef<
  MaterialRatesDataGridHandler,
  MaterialRatesDataGridProps
>(function MaterialRatesDataGrid(
  { companyId, customerId, materialIds, onDelete, onEdit, projectIds, siteIds },
  ref,
) {
  const { currencyFormatter } = useCompanyCurrency();

  const { getCompanyMaterialRates } = useMaterialRates();

  const [materialRatesGridState, setMaterialRatesGridState] =
    useState<MaterialRateGridState>({
      isLoading: true,
      materialRates: [],
    });

  const [dataGridPagination, setDataGridPagination] = useState<Pagination>({
    limit: MAX_API_PAGE_SIZE,
    page: 1,
  });

  const columns = useMemo(() => {
    const columns: Array<DataTableColumn<MaterialRate>> = [
      {
        field: 'project',
        headerName: t('form_fields.project'),
        valueGetter: (materialRate) => materialRate.project?.name,
      },
      {
        field: 'material',
        headerName: t('form_fields.material'),
        valueGetter: (materialRate) => materialRate.material.name,
      },
      {
        field: 'unit',
        headerName: t('form_fields.unit'),
        valueGetter: (materialRate) => materialRate.unitOfMeasure,
      },
      {
        field: 'site',
        headerName: t('form_fields.site'),
        valueGetter: (materialRate) => materialRate.site?.name,
      },
      {
        field: 'buyPrice',
        headerName: t('administration.material_rates.buy_price'),
        renderCell: (materialRate) => (
          <Typography sx={{ fontSize: '12px' }}>
            {isNil(materialRate.buyPrice) ? '' : currencyFormatter(materialRate.buyPrice)}
          </Typography>
        ),
      },
      {
        field: 'sellPrice',
        headerName: t('administration.material_rates.sell_price'),
        renderCell: (materialRate) => (
          <Typography sx={{ fontSize: '12px' }}>
            {isNil(materialRate.sellPrice)
              ? ''
              : currencyFormatter(materialRate.sellPrice)}
          </Typography>
        ),
      },
      actionsColumn((materialRate) => {
        const options = [
          {
            callBack: () => onDelete(materialRate),
            icon: <Delete />,
            title: t('actions.delete'),
          },
        ];

        return (
          <>
            <Tooltip title={t('administration.material_rates.edit_material_rate_cta')}>
              <IconButton onClick={() => onEdit(materialRate)} size="small">
                <Edit />
              </IconButton>
            </Tooltip>

            <SimpleMenu options={options} size="small">
              <MoreHoriz />
            </SimpleMenu>
          </>
        );
      }),
    ];

    return columns;
  }, [currencyFormatter, onDelete, onEdit]);

  const fetchMaterialRates = useCallback(async () => {
    try {
      const { data, pagination } = await getCompanyMaterialRates({
        companyId,
        customerId,
        defaultOnly: !customerId,
        materialIds,
        pagination: dataGridPagination,
        projectIds,
        siteIds,
      });

      setMaterialRatesGridState({ isLoading: false, materialRates: data, pagination });
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.material_rates.failed_to_fetch_material_rates'),
      );
    }
  }, [companyId, materialIds, siteIds, projectIds, customerId, dataGridPagination]);

  useEffect(() => {
    fetchMaterialRates();
  }, [fetchMaterialRates]);

  useImperativeHandle(
    ref,
    () => ({
      refetch() {
        fetchMaterialRates();
      },
    }),
    [fetchMaterialRates],
  );

  return (
    <>
      {/* No toolbar to ride in on, so the pager gets its own row. */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <DataGridPager
          isFetching={materialRatesGridState.isLoading}
          onNavigate={(link) => {
            if (link === 'after') {
              setDataGridPagination((state) => ({
                page: state.page + 1,
                limit: MAX_API_PAGE_SIZE,
                after: materialRatesGridState.pagination?.after,
              }));
            } else if (link === 'before') {
              setDataGridPagination((state) => ({
                page: state.page - 1,
                limit: MAX_API_PAGE_SIZE,
                before: materialRatesGridState.pagination?.before,
              }));
            } else {
              setDataGridPagination({ limit: MAX_API_PAGE_SIZE, page: 1 });
            }
          }}
          pagination={{
            ...dataGridPagination,
            before: materialRatesGridState.pagination?.before,
            after: materialRatesGridState.pagination?.after,
          }}
        />
      </Box>
      <DataTable
        id="material-rates-grid"
        columns={columns}
        hideToolbar
        loading={materialRatesGridState.isLoading}
        rows={materialRatesGridState.materialRates}
      />
    </>
  );
});

export default MaterialRatesDataGrid;
